import type { ComputedRef, Ref, WatchSource } from 'vue'
import { useRealtimeRefresh } from '@/composables/useRealtimeRefresh'
import { computed, ref, watch, onUnmounted } from 'vue'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import { isNullOrUndefined } from '@core/utils/helpers'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

export type FilterFieldType = 'tree' | 'number' | 'text' | 'search' | 'status' | 'boolean' | 'date_single' | 'date_range'
export type DateFormatType = 'YYYY-MM-DD' | 'Date' | 'ISO'

// Вспомогательная функция для форматирования даты в чипах
const formatDateChip = (dateStr: any) => {
  if (!dateStr) return ''
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return String(dateStr)
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`
  } catch (e) {
    return String(dateStr)
  }
}

// Вспомогательная функция разворачивания
const unref = <T>(val: T | Ref<T> | ComputedRef<T>): T => {
  return ((val as any).value !== undefined ? (val as any).value : val) as T
}

/**
 * Конфигурация отдельного поля фильтра
 */
export interface IFilterConfig {
  key: string // Ключ поля в объекте filters (например, 'departments')
  labelPrefix: string // Текст префикса в чипе (например, 'Подразделение: ')
  type: FilterFieldType // Тип фильтра для определения способа парсинга лейбла и рендеринга

  // Дополнительно для двойных дат (range) при едином меню (как в AuditNonconfrormities)
  rangeStartKey?: string // 'created_after'
  rangeEndKey?: string // 'created_before'

  // Приведение типа даты для API (решает проблему Payload Type Mismatch)
  dateFormat?: DateFormatType

  // Кастомная функция лейбла (решает проблему изоляции данных - coupling статусов)
  getCustomLabel?: (value: any) => string

  // Тихий фильтр: синхронизируется с query URL и F5, но не создаёт чип и не активирует кнопку «Сбросить фильтры»
  silent?: boolean
}

/**
 * Описание чипа активного фильтра с гарантированным уникальным идентификатором
 */
export interface ITableFilterChip {
  id?: string
  key?: string
  label: string
  type: string
  value?: any
  config?: IFilterConfig
}

/**
 * Основные параметры инициализации Composable
 */
export interface IUseTableFiltersOptions<TFilters> {
  // Передаем сам объект с фильтрами по ссылке
  filters: Ref<TFilters> | TFilters

  // Источники дерева для генерации чипов (универсальный поиск)
  searchChoices?: Record<string, any[]>
  filterChoices?: Record<string, any[]>

  // 1. Загрузка данных фильтров (Tree Loading)
  fetchFilterChoices?: (fieldKey: string) => Promise<void>

  // 2. Стейт индикаторов загрузки (Spinners)
  filterLoading?: Record<string, boolean>

  // Строго типизированный коллбэк для вызова API обновления таблицы
  fetchData: (cursor?: string, limit?: number, throwOnError?: boolean) => Promise<void>

  realtimeEnabled?: boolean
  realtimeRefresh?: () => Promise<void>
  realtimeRevision?: WatchSource<number>

  // 3. Синхронизация состояния пагинации и сортировки (Refs)
  currentSortString: Ref<string | null>
  itemsPerPage: Ref<number>

  // Делегирование сброса фильтров стору (Reset Delegation), чтобы не стереть дефолтные значения
  resetFilters: () => void

  // 4. Решение проблемы пагинации: доступ к курсору и длине для Infinite Scroll (Data Access)
  nextCursor: Ref<string | null>
  loadedItemsCount: Ref<number> | ComputedRef<number> // Количество уже загруженных элементов
  totalItemsCount: Ref<number> | ComputedRef<number> // Общее количество элементов на сервере

  // Маппинг ключей таблицы на ключи API для сортировки
  sortMap: Record<string, string>

  // Массив конфигураций для каждого фильтруемого поля
  filterConfigs: IFilterConfig[]

  // Флаг сброса фильтров при размонтировании
  resetOnUnmount?: boolean
}

/**
 * Composable `useTableFilters` - Универсальная логика для табличных компонентов (DRY)
 */
export function useTableFilters<TFilters extends Record<string, any>>(options: IUseTableFiltersOptions<TFilters>) {
  // Извлекаем объект filters (разворачиваем Ref если он передан как Ref, либо используем как есть если реактивный объект стора)
  const filtersObj = computed(() => {
    // @ts-expect-error
    return typeof options.filters.value !== 'undefined' ? options.filters.value : options.filters
  })

  // 1. Защищенные от коллизий состояния
  const filterMenus = ref<Record<string, boolean>>({})
  const tempValues = ref<Record<string, any>>({})
  const tempSelectedFilters = ref<Record<string, any[]>>({})

  // 2. Глобальный поиск
  const searchFilter = ref('')

  // 3. Состояние модалки сотрудника
  const isEmployeeInfoModalOpen = ref(false)
  const selectedEmployeeId = ref('')

  const showEmployeeInfoModal = (id: string | null | undefined) => {
    if (!id) return
    selectedEmployeeId.value = id
    isEmployeeInfoModalOpen.value = true
  }

  // 4. Состояние пагинации
  const isLoadingMore = ref(false)
  const currentPage = ref(1)

  // 4.1. Синхронизация фильтров с параметрами URL (vue-router)
  let route: any = null
  let router: any = null
  const isLeavingPage = ref(false)
  try {
    route = useRoute()
    router = useRouter()
    onBeforeRouteLeave(() => {
      isLeavingPage.value = true
    })
  } catch (e) {
    // Игнорируем вне контекста vue-router
  }

  // Вспомогательная функция для парсинга значений из URL query в типы фильтров
  const parseQueryValue = (value: any, type: FilterFieldType, key: string, config: IFilterConfig) => {
    if (value === undefined || value === null) return null
    if (type === 'tree' || type === 'number' || type === 'status') {
      const arr = String(value).split(',').filter(Boolean)
      if (type === 'number') {
        return arr.map(Number).filter(n => !isNaN(n))
      }
      return arr
    }
    if (type === 'text') {
      return String(value)
    }
    if (type === 'boolean') {
      return value === 'true'
    }
    if (type === 'date_range' || type === 'date_single') {
      const d = new Date(value)
      if (isNaN(d.getTime())) return null

      const currentVal = filtersObj.value?.[key]
      if (typeof currentVal === 'string' || config.dateFormat === 'YYYY-MM-DD') {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      }
      return d
    }
    return value
  }

  // Загрузка фильтров из query URL
  const loadFiltersFromQuery = () => {
    if (!route || !route.query) return
    const f = filtersObj.value
    if (!f) return

    options.filterConfigs.forEach(config => {
      if (config.type === 'date_range' && config.rangeStartKey && config.rangeEndKey) {
        if (route.query[config.rangeStartKey] !== undefined) {
          f[config.rangeStartKey] = parseQueryValue(route.query[config.rangeStartKey], config.type, config.rangeStartKey, config)
          tempValues.value[config.rangeStartKey] = formatDateChip(f[config.rangeStartKey])
        }
        if (route.query[config.rangeEndKey] !== undefined) {
          f[config.rangeEndKey] = parseQueryValue(route.query[config.rangeEndKey], config.type, config.rangeEndKey, config)
          tempValues.value[config.rangeEndKey] = formatDateChip(f[config.rangeEndKey])
        }
        return
      }

      const qVal = route.query[config.key]
      if (qVal !== undefined) {
        const parsedVal = parseQueryValue(qVal, config.type, config.key, config)
        f[config.key] = parsedVal
        if (config.type === 'number') {
          tempValues.value[config.key] = Array.isArray(f[config.key]) ? f[config.key].join(', ') : f[config.key]
        } else if (config.type === 'text') {
          tempValues.value[config.key] = f[config.key] || ''
        } else if (config.type === 'date_single') {
          tempValues.value[config.key] = formatDateChip(f[config.key])
        }

        // Загружаем опции фильтра с сервера, чтобы чипы могли отобразить человеческие названия вместо ID
        if (options.fetchFilterChoices && (config.type === 'tree' || config.type === 'status')) {
          const hasValue = Array.isArray(parsedVal) ? parsedVal.length > 0 : !isNullOrUndefined(parsedVal) && parsedVal !== ''
          if (hasValue) {
            options.fetchFilterChoices(config.key).catch((err: any) => {
              console.error(`Ошибка предзагрузки опций для фильтра ${config.key}:`, err)
            })
          }
        }
      }
    })

    if (route.query.search !== undefined) {
      f.search = String(route.query.search)
      searchFilter.value = f.search
    }
  }

  // Запускаем синхронную загрузку фильтров из query при инициализации
  loadFiltersFromQuery()

  // Сохранение фильтров в query URL при изменениях
  const updateQueryFromFilters = () => {
    if (!router || !route || isLeavingPage.value) return
    const f = filtersObj.value
    if (!f) return

    const newQuery: Record<string, string | undefined> = {}

    options.filterConfigs.forEach(config => {
      if (config.type === 'date_range' && config.rangeStartKey && config.rangeEndKey) {
        const startVal = f[config.rangeStartKey]
        const endVal = f[config.rangeEndKey]
        if (startVal instanceof Date) {
          newQuery[config.rangeStartKey] = startVal.toISOString()
        } else if (startVal) {
          newQuery[config.rangeStartKey] = String(startVal)
        }
        if (endVal instanceof Date) {
          newQuery[config.rangeEndKey] = endVal.toISOString()
        } else if (endVal) {
          newQuery[config.rangeEndKey] = String(endVal)
        }
        return
      }

      const val = f[config.key]
      if (isNullOrUndefined(val)) return
      if (Array.isArray(val) && val.length === 0) return
      if (typeof val === 'string' && val === '') return

      if (Array.isArray(val)) {
        newQuery[config.key] = val.join(',')
      } else if (val instanceof Date) {
        newQuery[config.key] = val.toISOString()
      } else {
        newQuery[config.key] = String(val)
      }
    })

    if (f.search) {
      newQuery.search = String(f.search)
    }

    const currentQuery = { ...route.query }

    // Удаляем пустые из currentQuery для точного сравнения
    Object.keys(currentQuery).forEach(k => {
      if (currentQuery[k] === undefined) delete currentQuery[k]
    })

    let isChanged = false
    const allKeys = new Set([...Object.keys(newQuery), ...Object.keys(currentQuery)])
    for (const key of allKeys) {
      const isFilterField = options.filterConfigs.some(c => c.key === key || c.rangeStartKey === key || c.rangeEndKey === key) || key === 'search'
      if (isFilterField) {
        if (String(newQuery[key] ?? '') !== String(currentQuery[key] ?? '')) {
          isChanged = true
        }
      } else {
        // Сохраняем остальные query параметры
        newQuery[key] = currentQuery[key] as string
      }
    }

    if (isChanged) {
      router.replace({ query: newQuery }).catch((err: any) => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err)
        }
      })
    }
  }

  // Следим за изменениями фильтров в сторе для обновления query в URL
  watch(
    () => filtersObj.value,
    () => {
      updateQueryFromFilters()
    },
    { deep: true },
  )

  // 5. Вычисляемые свойства

  // Проверка на наличие фильтров (для отображения кнопки сброса)
  const hasActiveFilters = computed(() => {
    const f = filtersObj.value
    if (!f) return false

    // Проверяем все ключи из конфига
    const hasConfigFilter = options.filterConfigs.some(config => {
      if (config.silent) return false

      // Исключение для составных дат (range)
      if (config.type === 'date_range' && config.rangeStartKey && config.rangeEndKey) {
        return !isNullOrUndefined(f[config.rangeStartKey]) || !isNullOrUndefined(f[config.rangeEndKey])
      }

      const val = f[config.key]

      if (config.type === 'boolean') {
        return !isNullOrUndefined(val)
      }

      if (Array.isArray(val)) {
        return val.length > 0
      }

      return !isNullOrUndefined(val) && val !== ''
    })

    return hasConfigFilter || (f.search && f.search !== '')
  })

  // Вспомогательная функция для рекурсивного поиска лейблов в дереве
  const findLabelRecursive = (nodes: any[], id: any): string | undefined => {
    if (!nodes || !Array.isArray(nodes)) return undefined

    for (const node of nodes) {
      if (String(node.id) === String(id) || String(node.value) === String(id)) {
        return node.label || node.name || node.full_name || node.title || node.short_name || String(id)
      }
      if (node.children && node.children.length > 0) {
        const found = findLabelRecursive(node.children, id)
        if (found) return found
      }
      // Поддержка childrens для обратной совместимости с модулями Документы и Журналы
      if (node.childrens && node.childrens.length > 0) {
        const found = findLabelRecursive(node.childrens, id)
        if (found) return found
      }
    }
    return undefined
  }

  // Вспомогательная функция извлечения лейбла
  const getLabel = (field: string, id: any) => {
    const searchCh = unref(options.searchChoices)
    const filterCh = unref(options.filterChoices)

    let label = findLabelRecursive(searchCh?.[field] || [], id)
    if (!label) {
      label = findLabelRecursive(filterCh?.[field] || [], id)
    }
    return label || String(id)
  }

  // Динамическая генерация массива чипсов
  const activeFilterChips = computed(() => {
    const chips: Array<ITableFilterChip> = []
    const f = filtersObj.value

    if (!f) return chips

    options.filterConfigs.forEach(config => {
      if (config.silent) return

      // Исключение для составной даты (date_range) с отдельными ключами start/end
      if (config.type === 'date_range' && config.rangeStartKey && config.rangeEndKey) {
        const startVal = f[config.rangeStartKey]
        const endVal = f[config.rangeEndKey]

        let label = ''
        if (startVal && endVal) label = `с ${formatDateChip(startVal)} по ${formatDateChip(endVal)}`
        else if (startVal) label = `с ${formatDateChip(startVal)}`
        else if (endVal) label = `по ${formatDateChip(endVal)}`

        if (label) {
          const chipKey = `${config.key}-${startVal || ''}-${endVal || ''}`
          chips.push({
            id: chipKey,
            key: chipKey,
            label: `${config.labelPrefix}${label}`,
            type: config.type,
            config,
          })
        }
        return // Пропускаем стандартную обработку
      }

      const val = f[config.key]

      if (isNullOrUndefined(val)) return
      if (Array.isArray(val) && val.length === 0) return
      if (typeof val === 'string' && val === '') return

      if (config.type === 'tree' && Array.isArray(val)) {
        val.forEach((id: any) => {
          // Используем getCustomLabel, чтобы избежать показа UUID для кастомных полей Tree
          const labelText = config.getCustomLabel ? config.getCustomLabel(id) : getLabel(config.key, id)
          const chipKey = `${config.key}-${id}`
          chips.push({
            id: chipKey,
            key: chipKey,
            label: `${config.labelPrefix}${labelText}`,
            type: config.type,
            value: id,
            config,
          })
        })
      } else if (config.type === 'status') {
        const getLabelText = (v: any) => config.getCustomLabel ? config.getCustomLabel(v) : String(v)
        if (Array.isArray(val)) {
          val.forEach((statusVal: any) => {
            const chipKey = `${config.key}-${statusVal}`
            chips.push({
              id: chipKey,
              key: chipKey,
              label: `${config.labelPrefix}${getLabelText(statusVal)}`,
              type: config.type,
              value: statusVal,
              config,
            })
          })
        } else {
          const chipKey = `${config.key}-${val}`
          chips.push({
            id: chipKey,
            key: chipKey,
            label: `${config.labelPrefix}${getLabelText(val)}`,
            type: config.type,
            value: val,
            config,
          })
        }
      } else if (config.type === 'boolean') {
        const labelText = config.getCustomLabel
          ? config.getCustomLabel(val)
          : `${config.labelPrefix}${val ? 'Да' : 'Нет'}`
        const chipKey = `${config.key}-${val}`
        chips.push({
          id: chipKey,
          key: chipKey,
          label: labelText,
          type: config.type,
          value: val,
          config,
        })
      } else if (config.type === 'date_single') {
        const chipKey = `${config.key}-${val}`
        chips.push({
          id: chipKey,
          key: chipKey,
          label: `${config.labelPrefix}${formatDateChip(val)}`,
          type: config.type,
          value: val,
          config,
        })
      } else {
        // Fallback для остальных типов (number, etc)
        const chipKey = `${config.key}-${val}`
        chips.push({
          id: chipKey,
          key: chipKey,
          label: `${config.labelPrefix}${val}`,
          type: config.type,
          value: val,
          config,
        })
      }
    })

    // Универсальный поиск (search)
    if (f.search) {
      chips.push({
        id: `search-${f.search}`,
        key: `search-${f.search}`,
        label: `Поиск: ${f.search}`,
        type: 'search',
      })
    }

    return chips
  })

  // Логика скрытия кнопки Next в пагинации (Infinite Scroll pagination footer)
  const hideNextButton = computed(() => {
    const isFetchReady = unref(options.nextCursor) === null
    const isMaxPage = Math.ceil(unref(options.totalItemsCount) / options.itemsPerPage.value) <= currentPage.value
    return isFetchReady || isMaxPage
  })

  // 6. Методы-обработчики

  /**
   * Возвращает класс иконки (закрашенную или пустую контурную воронку)
   */
  const getFilterIcon = (fieldKey: string) => {
    const f = filtersObj.value
    if (!f) return 'ri-filter-2-line'

    // Поиск конфига по ключу для обработки составных дат
    const config = options.filterConfigs.find(c => c.key === fieldKey)
    if (config?.type === 'date_range' && config.rangeStartKey && config.rangeEndKey) {
      if (!isNullOrUndefined(f[config.rangeStartKey]) || !isNullOrUndefined(f[config.rangeEndKey])) {
        return 'ri-filter-fill'
      }
      return 'ri-filter-2-line'
    }

    const val = f[fieldKey]
    if (Array.isArray(val) && val.length > 0) return 'ri-filter-fill'
    if (!Array.isArray(val) && !isNullOrUndefined(val) && val !== '') return 'ri-filter-fill'

    return 'ri-filter-2-line'
  }

  /**
   * Открывает меню фильтра и вызывает загрузку дерева (если передана функция)
   */
  const handleFilterClick = async (fieldKey: string) => {
    if (!fieldKey) return

    const f = filtersObj.value

    // Синхронизируем временное состояние с текущим стором перед открытием
    if (f && Array.isArray(f[fieldKey])) {
      tempSelectedFilters.value[fieldKey] = [...f[fieldKey]]
    } else {
      tempSelectedFilters.value[fieldKey] = []
    }

    const config = options.filterConfigs.find(c => c.key === fieldKey)
    if (options.fetchFilterChoices && (config?.type === 'tree' || config?.type === 'status')) {
      // Игнорируем спиннер тут, он должен управляться консьюмером в filterLoading
      await options.fetchFilterChoices(fieldKey)
    }

    // Открываем конкретное меню
    filterMenus.value[fieldKey] = true
  }

  /**
   * Применяет выбранные фильтры дерева (Tree) и запрашивает данные
   */
  const applyFilter = (fieldKey: string) => {
    if (!fieldKey) return
    const f = filtersObj.value
    if (f) {
      f[fieldKey] = [...(tempSelectedFilters.value[fieldKey] || [])]
    }
    filterMenus.value[fieldKey] = false
    triggerServerFilter()
  }

  /**
   * Применяет числовой/строковый фильтр (Input) и запрашивает данные
   */
  const applyNumberFilter = (fieldKey: string) => {
    if (!fieldKey) return
    const f = filtersObj.value
    if (f) {
      f[fieldKey] = tempValues.value[fieldKey]
    }
    filterMenus.value[fieldKey] = false
    triggerServerFilter()
  }

  /**
   * Применяет дату (Single или Range).
   * Range подразумевает, что ключи лежат в rangeStartKey / rangeEndKey (а не в самом fieldKey).
   */
  const applyDateFilter = (fieldKey: string) => {
    if (!fieldKey) return
    const f = filtersObj.value

    const config = options.filterConfigs.find(c => c.key === fieldKey)

    if (config?.type === 'date_range' && config.rangeStartKey && config.rangeEndKey) {
      // Даты уже находятся в `tempValues` по ключам `rangeStartKey` / `rangeEndKey`
      if (f) {
        f[config.rangeStartKey] = tempValues.value[config.rangeStartKey]
        f[config.rangeEndKey] = tempValues.value[config.rangeEndKey]
      }
    } else {
      if (f) {
        f[fieldKey] = tempValues.value[fieldKey]
      }
    }

    filterMenus.value[fieldKey] = false
    triggerServerFilter()
  }

  const resetDateFilter = (fieldKey: string) => {
    if (!fieldKey) return
    const config = options.filterConfigs.find(c => c.key === fieldKey)

    if (config?.type === 'date_range' && config.rangeStartKey && config.rangeEndKey) {
      tempValues.value[config.rangeStartKey] = null
      tempValues.value[config.rangeEndKey] = null
    } else {
      tempValues.value[fieldKey] = null
    }
  }

  const setDatePreset = (preset: { start?: string; end?: string }, fieldKey: string) => {
    const config = options.filterConfigs.find(c => c.key === fieldKey)
    if (config?.type === 'date_range' && config.rangeStartKey && config.rangeEndKey) {
      tempValues.value[config.rangeStartKey] = preset.start || null
      tempValues.value[config.rangeEndKey] = preset.end || null
    } else {
      tempValues.value[fieldKey] = preset.start // Fallback для сингл-даты
    }
  }

  /**
   * Применяет глобальный поиск
   */
  const applySearchFilter = () => {
    if (searchFilter.value.trim() === '') return
    const f = filtersObj.value
    if (f) {
      f.search = searchFilter.value
    }
    searchFilter.value = ''
    triggerServerFilter()
  }

  /**
   * Удаляет чип из UI и синхронизирует состояние (включая сложные range/array)
   */
  const removeFilter = (chip: ITableFilterChip | { label: string; type: string; value?: any; config?: IFilterConfig }) => {
    if (chip.type === 'search') {
      searchFilter.value = ''
      const f = filtersObj.value
      if (f) f.search = ''
      triggerServerFilter()
      return
    }

    const config = chip.config
    if (!config) return
    const f = filtersObj.value
    if (!f) return

    if (config.type === 'date_range' && config.rangeStartKey && config.rangeEndKey) {
      f[config.rangeStartKey] = null
      f[config.rangeEndKey] = null
      tempValues.value[config.rangeStartKey] = null
      tempValues.value[config.rangeEndKey] = null
    } else if (config.type === 'tree' || Array.isArray(f[config.key])) {
      // Если chip.value — массив (один чип представляет весь массив, например
      // фильтр по номерам "№: 205,206,207"), очищаем массив целиком.
      // Иначе (каждый элемент — отдельный чип, например tree) — убираем один.
      if (Array.isArray(chip.value)) {
        f[config.key] = []
        if (tempValues.value[config.key] !== undefined) {
          tempValues.value[config.key] = ''
        }
      } else {
        f[config.key] = f[config.key].filter((v: any) => String(v) !== String(chip.value))
      }
    } else if (config.type === 'date_single') {
      f[config.key] = null
      tempValues.value[config.key] = null
    } else if (config.type === 'text') {
      f[config.key] = ''
      tempValues.value[config.key] = ''
    } else if (config.type === 'status') {
      if (Array.isArray(f[config.key])) {
        f[config.key] = f[config.key].filter((v: any) => String(v) !== String(chip.value))
      } else {
        f[config.key] = ''
      }
    } else {
      f[config.key] = null
      tempValues.value[config.key] = null
    }

    triggerServerFilter()
  }

  /**
   * Очищаем фильтры при демонтировании компонента (уход на другой роут)
   */
  onUnmounted(() => {
    isLeavingPage.value = true
    if (options.resetOnUnmount !== false) {
      options.resetFilters()
    }
  })

  /**
   * Делегирует очистку в стор ([Reset Delegation]), чтобы НЕ стереть дефолтные значения (null, empty arrays, presets),
   * а затем перезапрашивает данные с сервера.
   */
  const renewDataAndResetFilters = () => {
    options.resetFilters() // Вызов нативного метода стора
    tempValues.value = {}
    tempSelectedFilters.value = {}
    searchFilter.value = ''
    triggerServerFilter()
  }

  const triggerServerFilter = async () => {
    currentPage.value = 1
    const feedbackStore = useFeedbackStore()
    feedbackStore.isGlobalLoading = true
    try {
      await options.fetchData(undefined, options.itemsPerPage.value)
    } finally {
      feedbackStore.isGlobalLoading = false
    }
  }

  /**
   * Обработчик пагинации компонента Vuetify
   */
  const handlePageChange = async (pageOptions: any) => {
    // pageOptions из Vuetify: { page, itemsPerPage, sortBy }
    const page = Number(pageOptions.page || 1)
    const perPage = Number(pageOptions.itemsPerPage || 25)

    let newSortString: string | null = null

    if (pageOptions.sortBy && pageOptions.sortBy.length > 0) {
      const { key, order } = pageOptions.sortBy[0]
      const backendKey = options.sortMap[key]
      if (backendKey) {
        newSortString = order === 'desc' ? `-${backendKey}` : backendKey
      }
    }

    currentPage.value = page

    // 1. Сортировка или изменение лимита
    if (newSortString !== options.currentSortString.value || perPage !== options.itemsPerPage.value) {
      options.currentSortString.value = newSortString
      options.itemsPerPage.value = perPage
      currentPage.value = 1
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await options.fetchData(undefined, perPage)
      } finally {
        feedbackStore.isGlobalLoading = false
      }
      return
    }

    // 2. Infinite scroll логика
    const itemsNeeded = page * perPage
    const currentLoaded = unref(options.loadedItemsCount)

    if (isLoadingMore.value) return

    if (itemsNeeded > currentLoaded && unref(options.nextCursor)) {
      isLoadingMore.value = true
      let cursorParam: string | null = null

      try {
        const urlStr = unref(options.nextCursor) as string
        cursorParam = new URL(urlStr).searchParams.get('cursor')
      } catch (e) {
        cursorParam = unref(options.nextCursor) as string
      }

      options.fetchData(cursorParam ?? undefined, options.itemsPerPage.value)
        .catch(err => console.error('Ошибка догрузки данных таблицы:', err))
        .finally(() => {
          isLoadingMore.value = false
        })
    }
  }

  // Хелперы для иконки сортировки в шаблонах таблиц
  const getSortIcon = (colKey: string): string => {
    const backendKey = options.sortMap[colKey]
    const current = options.currentSortString.value?.replace('-', '')
    return current === backendKey ? 'ri-arrow-up-fill' : 'ri-arrow-up-line'
  }

  const isSortActive = (colKey: string): boolean => {
    const backendKey = options.sortMap[colKey]
    return options.currentSortString.value?.replace('-', '') === backendKey
  }

  const isSortDesc = (colKey: string): boolean => {
    const backendKey = options.sortMap[colKey]
    return options.currentSortString.value === `-${backendKey}`
  }

  let recoveryTargetRows = 0
  let recoveryQuery = ''
  if (options.realtimeEnabled || options.realtimeRefresh) useRealtimeRefresh(async () => {
    if (isLeavingPage.value) return
    if (isLoadingMore.value) throw new Error('Table page request in progress')
    const query = () => JSON.stringify([filtersObj.value, options.currentSortString.value, options.itemsPerPage.value])
    const initialQuery = query()
    if (recoveryQuery !== initialQuery) recoveryTargetRows = 0
    recoveryQuery = initialQuery
    recoveryTargetRows = Math.max(recoveryTargetRows, unref(options.loadedItemsCount), currentPage.value * options.itemsPerPage.value)
    isLoadingMore.value = true
    console.warn(`📊 [Table Recovery] Сработало восстановление таблицы! Целевое количество строк: ${recoveryTargetRows}`)
    try {
      if (options.realtimeRefresh) {
        console.log('📊 [Table Recovery] Вызов пользовательского realtimeRefresh()...')
        await options.realtimeRefresh()
      } else {
        console.log('📊 [Table Recovery] Перезапрос первой страницы таблицы через fetchData()...')
        await options.fetchData(undefined, options.itemsPerPage.value, true)
        const seen = new Set<string>()
        while (!isLeavingPage.value && query() === initialQuery && unref(options.loadedItemsCount) < recoveryTargetRows && unref(options.nextCursor)) {
          const raw = unref(options.nextCursor) as string
          const cursor = raw.includes('?') ? new URL(raw, window.location.origin).searchParams.get('cursor') : raw
          if (!cursor || seen.has(cursor)) break
          seen.add(cursor)
          console.log(`📄 [Table Recovery] Догрузка следующей страницы по курсору "${cursor}" (загружено ${unref(options.loadedItemsCount)} из ${recoveryTargetRows})...`)
          await options.fetchData(cursor, options.itemsPerPage.value, true)
        }
      }
      if (!isLeavingPage.value && query() === initialQuery)
        currentPage.value = Math.min(currentPage.value, Math.max(1, Math.ceil(unref(options.totalItemsCount) / options.itemsPerPage.value)))
      recoveryTargetRows = 0
      console.log('✅ [Table Recovery] Восстановление таблицы успешно завершено')
    } catch (err) {
      console.error('❌ [Table Recovery] Ошибка при восстановлении строк таблицы:', err)
      throw err
    } finally {
      isLoadingMore.value = false
    }
  }, options.realtimeRevision)

  return {
    // Состояния
    filterMenus,
    tempValues,
    tempSelectedFilters,
    searchFilter,

    // Модалки
    isEmployeeInfoModalOpen,
    selectedEmployeeId,
    showEmployeeInfoModal,

    // Пагинация
    isLoadingMore,
    currentPage,

    // Спиннеры (proxy)
    filterLoading: options.filterLoading,

    // Вычисляемые св-ва
    hasActiveFilters,
    activeFilterChips,
    hideNextButton,

    // Методы
    getFilterIcon,
    handleFilterClick,
    applyFilter,
    applyNumberFilter,
    applyDateFilter,
    resetDateFilter,
    setDatePreset,
    applySearchFilter,
    removeFilter,
    renewDataAndResetFilters,
    triggerServerFilter,
    handlePageChange,
    getSortIcon,
    isSortActive,
    isSortDesc,
  }
}
